import React, { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Slider, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import SEO from '../../shared/components/SEO';
import { BRAND_SANS_FONT } from '../../shared/design/theme';
import {
  LandingContainer,
  PricingGrid,
  PricingCard,
  PricingTier,
  PricingPrice,
  PricingNote,
  PricingFeatures,
  PricingFeature,
  PricingButton,
  BaseCard,
  SectionTitle,
  SectionSubtitle,
} from '../home/HomePage.styles';
import '../home/HomePage.styles.css';
import { Footer } from '../../shared/components/Footer';
import { SiteHeader } from '../../shared/components/SiteHeader';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';

const ACCENT = '#B83B1D';
const INK = '#2E140A';
const PRACTICE_MAILTO =
  'mailto:hi@re-stack.com?subject=Restack%20Practice%20Plan%20Inquiry&body=Hi%20Restack%2C%0A%0AI%27m%20interested%20in%20learning%20more%20about%20the%20Practice%20plan%20for%20our%20firm.%0A%0A';

// How credits and billing work. Kept factual and aligned with the billing
// system ($800 per seat, 800 monthly credits per seat, $1/credit booster
// top-ups, project-code receipts, billed monthly with no annual lock-in).
const PRICE_PER_SEAT = 800;
const CREDITS_PER_SEAT = 800;
// Self-serve seat ceiling. 6+ seats is sales-assisted (Practice / talk to us).
const MAX_SEATS = 5;

// One rhythm for the whole page. Every band below the hero opens on the same
// vertical gap, so the page reads as evenly spaced sections instead of a stack
// of one-off margins.
const SECTION_MT = { xs: 5, md: 7 };

// Proof line under the hero. The AppSource listing is announced in /news and
// the training-data row is in the Pro vs Practice table below. The model line
// names the families the in-product picker actually offers
// (src/screens/chat/ai-chat/constants.js), and it names MODELS rather than
// vendors on purpose: Gemini is reached through OpenRouter, so a partnership
// claim would be false where a model-family claim is not. Nothing here is a
// promise the product does not already keep.
const TRUST_SIGNALS = [
  'Listed on Microsoft AppSource',
  'Claude, GPT, or Gemini, your pick',
  'Never trained on your data',
];

// Risk reversal, stated in the two places it changes a decision: under the
// price, and next to the final CTA. Wording matches the support FAQ
// (faqChatService.js, id "refund") and the billing reality below.
const GUARANTEE_SHORT =
  'Every Pro seat carries a 30-day money-back guarantee. Billed monthly, cancel anytime, no annual lock-in.';
const GUARANTEE_LONG =
  'If Restack does not earn its place in your workflow within the first month, email hi@re-stack.com and we refund the subscription, no questions asked.';
// Pro vs Practice, Julius-style: the detail lives in the row label, the plan
// columns carry a tick (included), a muted dash (not included), or a terse
// value where a boolean cannot say it. Every row restates an existing public
// claim (the tier cards above and the Trust Center's "Available on Practice"
// list); nothing here is a new promise.
const COMPARE_GROUPS = [
  {
    group: 'Workspace & usage',
    rows: [
      { label: 'Seats', pro: '1 to 5, self-serve', practice: 'Unlimited' },
      { label: '800 credits per seat per month, pooled', pro: true, practice: 'Volume pricing' },
      { label: 'Works across PowerPoint, Excel, and Word', pro: true, practice: true },
      { label: 'Booster top-ups at $1 per credit', pro: true, practice: true },
    ],
  },
  {
    group: 'Security & admin',
    rows: [
      { label: 'Never trained on your data', pro: true, practice: true },
      { label: 'Sign in with Microsoft (Entra ID) or email', pro: true, practice: true },
      { label: 'Organization role-based access', pro: true, practice: true },
      { label: 'Centralized Microsoft 365 deployment', pro: true, practice: true },
      { label: 'Custom infrastructure and retention requirements', pro: false, practice: 'Contract review' },
      { label: 'Custom DPA and compliance terms', pro: false, practice: 'Contract review' },
    ],
  },
  {
    group: 'Billing & support',
    rows: [
      { label: 'Billed monthly, cancel anytime', pro: true, practice: true },
      { label: 'Engagement code on receipts', pro: true, practice: true },
      { label: 'Invoicing, POs, and annual agreements', pro: false, practice: true },
      { label: 'Founder-direct support, 24-hour reply', pro: true, practice: true },
      { label: 'Dedicated success manager', pro: false, practice: true },
    ],
  },
];

// Pricing-page FAQ: the approver's procedural questions, answered where the
// price is. Absorbs the former "How credits and billing work" cards so the
// page has ONE reference section. Also rendered as FAQPage JSON-LD. Answers
// must stay aligned with billing reality: rollover caps at two months'
// allotment, boosters carry 12 months, invoicing is a Practice motion.
const PRICING_FAQ = [
  {
    q: "What's a credit?",
    a: 'Roughly one AI action in any of the three apps: a slide built, a model cell sourced, a redline drafted, or a research pass.',
  },
  {
    q: 'What does a typical job cost in credits?',
    a: 'Typical usage is ~100-150 credits to build a full deck (~50 slides) from source material, ~50-100 to refresh a quarterly pack from a new model, and ~5-10 for either one sourced market-sizing slide or a research pass with citations.',
  },
  {
    q: 'Is this just resold API credits?',
    a: "No - and the math is checkable. Credits meter real model usage, and the agent's surgical-edit architecture means a deliverable usually consumes fewer tokens than the same work done by hand against a raw AI chat. The difference between the credit price and raw model cost pays for the machinery that makes the output committee-grade: self-verification, template enforcement, chart grammar, and source citations. You are buying the machine, metered by usage - not tokens at a markup.",
  },
  {
    q: 'How do monthly credits work?',
    a: 'Every seat includes 800 credits a month, pooled across your team and shared across all three apps. The pool refreshes at the start of each billing month.',
  },
  {
    q: 'What happens when credits run out?',
    a: 'The team keeps working: add booster top-ups at $1 per credit ($200 for 200 or $500 for 500). Boosters carry for 12 months.',
  },
  {
    q: 'Do unused credits roll over?',
    a: "Yes. Monthly credits roll over up to a cap of two months' allotment, and booster credits carry for 12 months.",
  },
  {
    q: 'Can I expense this to a client?',
    a: 'Yes. Receipts carry your project or engagement code, so the seat expenses cleanly to the engagement. Billed monthly, cancel anytime, no annual lock-in.',
  },
  {
    q: 'Can we pay by invoice or purchase order?',
    a: 'Yes, on Practice: invoicing with net-30 terms and POs, with annual agreements available. Email hi@re-stack.com and we set it up.',
  },
  {
    q: 'Is there a free trial?',
    a: 'We do something faster with your time: a 15-minute proof run where Restack rebuilds a real slide, model, or memo on your template, live. Pro also carries a 30-day money-back guarantee.',
  },
];

// Visually hidden but reachable by assistive tech.
const SR_ONLY = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const eyebrowSx = {
  fontFamily: BRAND_SANS_FONT,
  fontSize: '0.68rem',
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: ACCENT,
};
// One section-heading scale for the whole page. The three bands below the
// plans (compare table, the math, FAQ) previously ran at two different sizes
// and two different alignments, so they read as unrelated widgets rather than
// siblings. They now share this head.
const sectionHeadingSx = {
  fontFamily: BRAND_SANS_FONT,
  fontSize: { xs: '1.05rem', md: '1.2rem' },
  fontWeight: 600,
  letterSpacing: '-0.015em',
  lineHeight: 1.35,
  color: 'text.primary',
  textWrap: 'balance',
};
const bodySx = {
  fontFamily: BRAND_SANS_FONT,
  fontSize: '0.84rem',
  fontWeight: 400,
  lineHeight: 1.6,
  color: 'text.secondary',
};
// Shared button states. Focus-visible and active were missing everywhere on
// this page: a keyboard user had no idea where they were.
const btnStates = {
  transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease',
  '&:active': { transform: 'translateY(1px)' },
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: '3px' },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
};
const primaryBtn = {
  textTransform: 'none',
  fontFamily: BRAND_SANS_FONT,
  fontWeight: 600,
  fontSize: '0.85rem',
  letterSpacing: '-0.005em',
  borderRadius: '999px',
  px: 2.5,
  py: 0.9,
  color: '#FFFFFF',
  background: 'linear-gradient(135deg, #B83B1D 0%, #D4694E 100%)',
  boxShadow: '0 4px 14px rgba(184, 59, 29, 0.22)',
  '&:hover': {
    background: 'linear-gradient(135deg, #A63419 0%, #C25A42 100%)',
    boxShadow: '0 6px 18px rgba(184, 59, 29, 0.3)',
  },
  ...btnStates,
};
const secondaryBtn = {
  textTransform: 'none',
  fontFamily: BRAND_SANS_FONT,
  fontWeight: 500,
  fontSize: '0.85rem',
  letterSpacing: '-0.005em',
  borderRadius: '999px',
  px: 2.5,
  py: 0.9,
  color: 'text.primary',
  border: `1px solid ${alpha(INK, 0.2)}`,
  '&:hover': {
    backgroundColor: alpha(INK, 0.04),
    borderColor: alpha(INK, 0.32),
  },
  ...btnStates,
};

/**
 * Section head: rust eyebrow + h2, centered, on the page's single rhythm.
 *
 * Defined at module scope on purpose. Declared inside the component it would
 * be a fresh component type on every render, and this page re-renders on every
 * tick of the seat slider - which would remount the FAQ underneath it and snap
 * any open accordion shut mid-drag.
 */
function SectionHead({ eyebrow, title }) {
  return (
    <Box sx={{ mt: SECTION_MT, mb: 2.5, textAlign: 'center', maxWidth: 720, mx: 'auto' }}>
      <Typography sx={{ ...eyebrowSx, mb: 1 }}>{eyebrow}</Typography>
      <Typography component="h2" sx={sectionHeadingSx}>
        {title}
      </Typography>
    </Box>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();

  // Single per-seat plan: drag 1-5 seats, price and credits scale linearly.
  const [seats, setSeats] = useState(1);
  const monthly = seats * PRICE_PER_SEAT;
  const credits = seats * CREDITS_PER_SEAT;
  const seatLabel = `${seats} seat${seats > 1 ? 's' : ''}`;

  const pricingSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Restack',
    description:
      'AI for Private Equity teams: one plan covering PowerPoint, Excel, and Word on one shared credit pool.',
    brand: { '@type': 'Brand', name: 'Restack' },
    url: 'https://re-stack.com/pricing',
    offers: {
      '@type': 'Offer',
      name: 'Pro',
      price: '800',
      priceCurrency: 'USD',
      url: 'https://re-stack.com/pricing',
      availability: 'https://schema.org/InStock',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '800',
        priceCurrency: 'USD',
        unitText: 'seat per month',
        billingDuration: 'P1M',
        referenceQuantity: { '@type': 'QuantitativeValue', value: '1', unitText: 'seat' },
      },
    },
  };

  // FAQPage structured data mirrors the on-page PRICING_FAQ, so the rich
  // result and the rendered copy can never drift apart.
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: PRICING_FAQ.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LandingContainer data-theme="light" sx={{ minHeight: '100vh' }}>
      <SEO
        title="Pricing"
        description="Restack pricing for Private Equity teams: one plan at $800 per seat per month. One account covers PowerPoint, Excel, and Word on a shared credit pool."
        canonical="/pricing"
        schema={[pricingSchema, faqSchema]}
      />

      {/* Shared marketing header, matching the landing page. */}
      <SiteHeader />

      {/* Content. Top padding clears the fixed shared header. Carries the
          page's <main> landmark and the id the shared SkipLink targets. */}
      <Box
        component="main"
        id="main-content"
        sx={{
          maxWidth: 1160,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 'calc(56px + 1.5rem)', md: 'calc(64px + 2.5rem)' },
          pb: { xs: 6, md: 9 },
        }}
      >
        {/* Intro - centered, reads as an extension of the landing page. No
            eyebrow (the nav already says "Pricing"); the title borrows the
            landing pricing copy so the page focuses on the cards. The
            subheadline names the audience, which the headline alone did not. */}
        <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto' }}>
          <SectionTitle component="h1" sx={{ mb: 2, textAlign: 'center' }}>
            Priced below a single <em>analyst week</em>.
          </SectionTitle>
          <SectionSubtitle sx={{ mx: 'auto' }}>
            One plan for private equity deal teams: $800 per seat per month,
            billed to your engagement code. One account, one shared credit pool,
            covering PowerPoint, Excel, and Word.
          </SectionSubtitle>

          {/* Proof line. Restates claims the site already makes, so the price
              does not arrive without any standing behind it. */}
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              m: 0,
              mt: 2.5,
              p: 0,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
              columnGap: 1.5,
              rowGap: 0.75,
            }}
          >
            {TRUST_SIGNALS.map((signal, i) => (
              <Box
                key={signal}
                component="li"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  fontFamily: BRAND_SANS_FONT,
                  fontSize: '0.78rem',
                  lineHeight: 1.4,
                  color: 'text.secondary',
                }}
              >
                {i > 0 && (
                  <Box
                    aria-hidden="true"
                    sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: alpha(INK, 0.25) }}
                  />
                )}
                {signal}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Tier grid (moved off the home page so the landing stays high-level) */}
        <Box sx={{ mt: { xs: 4, md: 5 } }}>
          <PricingGrid sx={{ gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr' }, maxWidth: 900 }}>
            <PricingCard sx={{ borderColor: alpha(ACCENT, 0.4) }}>
              <PricingTier>Pro</PricingTier>
              <PricingPrice>
                ${monthly.toLocaleString()}
                <Box
                  component="small"
                  sx={{ fontSize: '1rem', fontWeight: 500, color: 'text.secondary' }}
                >
                  /mo
                </Box>
              </PricingPrice>
              {/* Live breakdown: current seat count + the $800 per-seat base, so
                  the total above is always self-explanatory as the slider moves. */}
              <Typography
                sx={{
                  fontFamily: BRAND_SANS_FONT,
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  mt: -0.5,
                  mb: 0.75,
                }}
              >
                <Box component="span" sx={{ color: ACCENT, fontWeight: 700 }}>{seatLabel}</Box>
                {' · $800 per seat'}
              </Typography>
              <PricingNote>One analyst or a full deal team. Drag to set your seats.</PricingNote>

              {/* Seat slider: price and credits scale linearly, 1 to 5 seats.
                  6+ seats is sales-assisted (Practice / talk to us). */}
              <Box sx={{ px: 0.5, mb: 2.5 }}>
                <Slider
                  value={seats}
                  onChange={(_, v) => setSeats(v)}
                  min={1}
                  max={MAX_SEATS}
                  step={1}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v} seat${v > 1 ? 's' : ''}`}
                  aria-label="Number of seats"
                  sx={{
                    color: ACCENT,
                    '& .MuiSlider-markActive': { backgroundColor: alpha('#FFFFFF', 0.7) },
                    '& .MuiSlider-valueLabel': {
                      backgroundColor: ACCENT,
                      fontFamily: BRAND_SANS_FONT,
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      borderRadius: '8px',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.25 }}>
                  <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.72rem', color: 'text.secondary' }}>
                    1 seat
                  </Typography>
                  <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.72rem', color: 'text.secondary' }}>
                    {MAX_SEATS} seats
                  </Typography>
                </Box>
              </Box>

              {/* Always visible (was gated behind dragging the slider to 5):
                  the self-serve boundary should be legible without discovering
                  it, Julius-style "more than N? contact us". */}
              <PricingNote>
                Self-serve covers 1 to 5 seats. Need 6+ or firm-wide?{' '}
                <Box
                  component="a"
                  href="mailto:hi@re-stack.com?subject=Restack%20Practice%20pricing%20for%20our%20team"
                  sx={{
                    color: ACCENT,
                    fontWeight: 700,
                    textDecoration: 'none',
                    borderRadius: '4px',
                    '&:hover': { textDecoration: 'underline', textUnderlineOffset: '0.18em' },
                    '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: '2px' },
                  }}
                >
                  Talk to us
                </Box>{' '}
                about Practice (volume pricing, pooled credits, invoicing).
              </PricingNote>

              <PricingFeatures>
                <PricingFeature index={0}>
                  <CheckRounded /> {credits.toLocaleString()} credits per month, pooled across the team
                </PricingFeature>
                <PricingFeature index={1}>
                  <CheckRounded /> {seatLabel} at $800/mo each
                </PricingFeature>
                <PricingFeature index={2}>
                  <CheckRounded /> Works across PowerPoint, Excel, and Word
                </PricingFeature>
                <PricingFeature index={3}>
                  <CheckRounded /> On your firm&rsquo;s template, master, and styles
                </PricingFeature>
                <PricingFeature index={4}>
                  <CheckRounded /> Research with citations, every figure sourced
                </PricingFeature>
                <PricingFeature index={5}>
                  <CheckRounded /> One shared pool, one account for the whole suite
                </PricingFeature>
                <PricingFeature index={6}>
                  <CheckRounded /> Sign in with Microsoft (Entra ID) or Google work accounts
                </PricingFeature>
                <PricingFeature index={7}>
                  <CheckRounded /> Engagement code on receipts
                </PricingFeature>
                <PricingFeature index={8}>
                  <CheckRounded /> Booster top-ups at $1 per credit
                </PricingFeature>
                <PricingFeature index={9}>
                  <CheckRounded /> Centralized Microsoft 365 deployment
                </PricingFeature>
              </PricingFeatures>
              <PricingButton
                variant="primary"
                onClick={() => navigate('/demo')}
                sx={btnStates}
              >
                See it on your own deck
              </PricingButton>
            </PricingCard>

            <PricingCard>
              <PricingTier>Practice</PricingTier>
              <PricingPrice>Custom</PricingPrice>
              <PricingNote>Roll out across your firm with centralized admin.</PricingNote>
              <PricingFeatures>
                <PricingFeature index={0} sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Everything in Pro, plus:
                </PricingFeature>
                <PricingFeature index={1}>
                  <CheckRounded /> Security and deployment requirements reviewed in contract
                </PricingFeature>
                <PricingFeature index={2}>
                  <CheckRounded /> Contracted projects and seat count
                </PricingFeature>
                <PricingFeature index={3}>
                  <CheckRounded /> Custom DPA and compliance terms
                </PricingFeature>
                <PricingFeature index={4}>
                  <CheckRounded /> Invoicing, POs, and annual agreements
                </PricingFeature>
                <PricingFeature index={5}>
                  <CheckRounded /> Dedicated success manager
                </PricingFeature>
              </PricingFeatures>
              <PricingButton
                variant="outline"
                onClick={() => { window.location.href = PRACTICE_MAILTO; }}
                sx={btnStates}
              >
                Contact us
              </PricingButton>
            </PricingCard>
          </PricingGrid>

          {/* Risk reversal, stated where the price objection peaks rather than
              only buried in the FAQ answer nine questions down. */}
          <Typography
            sx={{
              ...bodySx,
              mt: 2.5,
              mx: 'auto',
              maxWidth: 700,
              textAlign: 'center',
              color: alpha(INK, 0.6),
            }}
          >
            {GUARANTEE_SHORT}
          </Typography>
        </Box>

        {/* Pro vs Practice comparison - Julius-style: the detail lives in the
            row label, the plan columns carry ticks. Rendered open on the page
            theme (hairline rows, no tinted container) so it reads as part of
            the page, not a bolted-on widget. */}
        <SectionHead eyebrow="Plans" title="Compare Pro and Practice" />
        <Box sx={{ maxWidth: 860, mx: 'auto' }}>
          {/* Header row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '2fr 1fr 1fr', md: '2.4fr 1fr 1fr' },
              py: 1.25,
              borderBottom: `1px solid ${alpha(INK, 0.12)}`,
            }}
          >
            <Box />
            {['Pro', 'Practice'].map((t) => (
              <Typography
                key={t}
                sx={{
                  fontFamily: BRAND_SANS_FONT,
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  textAlign: 'center',
                  color: t === 'Practice' ? ACCENT : 'text.primary',
                }}
              >
                {t}
              </Typography>
            ))}
          </Box>
          {COMPARE_GROUPS.map((g) => (
            <Box key={g.group}>
              <Typography
                component="h3"
                sx={{
                  pt: 2.25,
                  pb: 0.75,
                  fontFamily: BRAND_SANS_FONT,
                  fontSize: '0.66rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: alpha(INK, 0.45),
                }}
              >
                {g.group}
              </Typography>
              {g.rows.map((r) => (
                <Box
                  key={r.label}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '2fr 1fr 1fr', md: '2.4fr 1fr 1fr' },
                    alignItems: 'center',
                    columnGap: { xs: 1, md: 2 },
                    py: 1.1,
                    borderBottom: `1px solid ${alpha(INK, 0.06)}`,
                  }}
                >
                  <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: { xs: '0.78rem', md: '0.84rem' }, lineHeight: 1.45, color: 'text.primary' }}>
                    {r.label}
                  </Typography>
                  {[r.pro, r.practice].map((cell, ci) => (
                    <Box key={ci} sx={{ display: 'flex', justifyContent: 'center' }}>
                      {/* The grid renders as divs, so a screen reader gets no
                          column association from the layout. Each cell states
                          its own plan and verdict instead: without this, a
                          tick and a dash both read as nothing at all. */}
                      <Box component="span" sx={SR_ONLY}>
                        {`${ci === 0 ? 'Pro' : 'Practice'}: `}
                        {cell === true ? 'included' : cell === false ? 'not included' : ''}
                      </Box>
                      {cell === true ? (
                        <CheckRounded sx={{ fontSize: 18, color: ACCENT }} />
                      ) : cell === false ? (
                        <Typography aria-hidden="true" component="span" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.84rem', color: alpha(INK, 0.25), lineHeight: 1 }}>
                          &ndash;
                        </Typography>
                      ) : (
                        <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: { xs: '0.74rem', md: '0.8rem' }, textAlign: 'center', lineHeight: 1.4, color: 'text.secondary' }}>
                          {cell}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        {/* ROI math - anchors the $800 seat against the cost of the analyst
            hours it returns. Figures kept defensible: $75-150/hr loaded
            analyst cost, 4-8 hrs saved per deck (same anchors as the
            /solutions stats), one deck a month covering the seat. */}
        <Box>
          <SectionHead
            eyebrow="The math"
            title="One deliverable a month, and the seat pays for itself."
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
              gap: { xs: 2, md: 2.5 },
            }}
          >
            {[
              { value: '$75-150/hr', body: 'Loaded cost of the analyst doing the research and formatting today.' },
              { value: '4-8 hrs per deck', body: 'Saved on one IC memo or market-sizing deck - the parts Restack builds.' },
              { value: '1 deliverable a month', body: 'Covers the $800 seat. Most teams run several a week.' },
              { value: 'What credits buy', body: '~5-10 for a sourced slide or research pass. ~50-100 for a pack refresh. ~100-150 for a full deck.' },
            ].map(({ value, body }) => (
              // BaseCard already stretches to the grid row (height: 100%), so
              // the four cards stay equal-height. The value slot reserves two
              // lines wherever cards sit side by side, so every supporting
              // paragraph starts on the same baseline no matter how the
              // heading wraps.
              <BaseCard
                key={value}
                sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column' }}
              >
                <Typography
                  component="div"
                  sx={{
                    fontFamily: BRAND_SANS_FONT,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: ACCENT,
                    fontSize: { xs: '1.4rem', md: '1.55rem' },
                    lineHeight: 1.15,
                    minHeight: { sm: '2.3em' },
                    textWrap: 'balance',
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                >
                  <span>{value}</span>
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    mt: 1,
                    fontFamily: BRAND_SANS_FONT,
                    fontSize: '0.84rem',
                    lineHeight: 1.6,
                    color: 'text.secondary',
                  }}
                >
                  {body}
                </Typography>
              </BaseCard>
            ))}
          </Box>
        </Box>

        {/* Pricing FAQ - dropdown accordions, Julius-style. Absorbs the
            former "How credits and billing work" cards and the standalone
            "what's a credit" line, so the page has one reference section.
            Also emitted as FAQPage JSON-LD via the SEO schema. */}
        <SectionHead eyebrow="FAQ" title="Pricing questions" />
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          {PRICING_FAQ.map(({ q, a }) => (
            <Accordion
              key={q}
              disableGutters
              elevation={0}
              square
              sx={{
                backgroundColor: 'transparent',
                borderBottom: `1px solid ${alpha(INK, 0.08)}`,
                '&::before': { display: 'none' },
              }}
            >
              {/* component="span" is deliberate: MUI v7 already wraps the
                  summary in its own heading element, so an h3 here would nest
                  a heading inside a heading. */}
              <AccordionSummary
                expandIcon={<ExpandMoreRounded sx={{ color: alpha(INK, 0.45) }} />}
                sx={{
                  px: 0.5,
                  minHeight: 52,
                  borderRadius: '8px',
                  transition: 'background-color 0.2s ease',
                  '& .MuiAccordionSummary-content': { my: 1 },
                  '&:hover': { backgroundColor: alpha(INK, 0.02) },
                  '&:hover .faq-question': { color: ACCENT },
                  '&:focus-visible': {
                    outline: `2px solid ${ACCENT}`,
                    outlineOffset: '-2px',
                    backgroundColor: 'transparent',
                  },
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}
              >
                <Typography
                  component="span"
                  className="faq-question"
                  sx={{
                    fontFamily: BRAND_SANS_FONT,
                    fontSize: '0.92rem',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: 'text.primary',
                    transition: 'color 0.2s ease',
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                >
                  {q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 2 }}>
                <Typography
                  sx={{
                    fontFamily: BRAND_SANS_FONT,
                    fontSize: '0.84rem',
                    lineHeight: 1.6,
                    color: 'text.secondary',
                  }}
                >
                  {a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Closing CTA. Same primary wording as the Pro card above, so the
            page asks for exactly one thing. The guarantee repeats here, on a
            hairline, because risk reversal earns its keep next to the action
            rather than as a separate box. */}
        <Box
          sx={{
            mt: SECTION_MT,
            p: { xs: 3, md: 4 },
            borderRadius: 2.5,
            textAlign: 'center',
            border: `1px solid ${alpha(INK, 0.09)}`,
            background: `linear-gradient(135deg, ${alpha(ACCENT, 0.05)} 0%, ${alpha('#FAF9F7', 0.6)} 100%)`,
          }}
        >
          <Typography component="h2" sx={{ ...sectionHeadingSx, mb: 1 }}>
            See it build real work first.
          </Typography>
          <Typography
            component="p"
            sx={{ ...bodySx, maxWidth: 540, mx: 'auto', mb: 2.5 }}
          >
            Judge the price by watching Restack build a slide on your own
            template, live, in 15 minutes.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.25,
              justifyContent: 'center',
            }}
          >
            <Button
              component={RouterLink}
              to="/demo"
              endIcon={<ArrowForwardRounded sx={{ fontSize: '1rem' }} />}
              sx={primaryBtn}
            >
              See it on your own deck
            </Button>
            <Button component={RouterLink} to="/product" sx={secondaryBtn}>
              Watch the 1-minute demo
            </Button>
          </Box>
          <Typography
            component="p"
            sx={{
              ...bodySx,
              mt: 3,
              pt: 2.5,
              maxWidth: 560,
              mx: 'auto',
              borderTop: `1px solid ${alpha(INK, 0.09)}`,
              color: alpha(INK, 0.6),
            }}
          >
            {GUARANTEE_LONG}
          </Typography>
        </Box>
      </Box>

      <Footer />
    </LandingContainer>
  );
}
